import type { Trait } from '../domain'

export interface DisplayExcerptAnchor {
  readonly trait: Trait
  readonly excerpt: string
  /** `sentence` means no isolated word is being claimed as the cause. */
  readonly basis: 'clause' | 'sentence'
  /** Inclusive UTF-16 offset into the unmodified sentence. */
  readonly start: number
  /** Exclusive UTF-16 offset into the unmodified sentence. */
  readonly end: number
}

interface TextBounds {
  readonly start: number
  readonly end: number
}

const MIN_EXCERPT_LENGTH = 8
const MAX_EXCERPT_LENGTH = 28
const PUNCTUATION = /[,.;!?…\n]/

/**
 * Display anchors only. This table is never scanned to discover a trait:
 * callers pass the already-final trait list, and these patterns merely find
 * a nearby piece of the player's own sentence to illuminate.
 */
const DISPLAY_ONLY_EXCERPT_ANCHORS: Readonly<Record<Trait, readonly RegExp[]>> = {
  bounded: [
    /(?:손바닥|고리|상자)\s*안[^,.;!?…\n]{0,14}(?:멎|멈추|닫|잠들|끝나)/,
    /(?:리본|가장자리|경계|범위|매듭)[^,.;!?…\n]{0,14}(?:멎|멈추|닫|잠들|끝나|수렴|종결)/,
  ],
  gentle: [
    /(?:숨결|천천히|부드럽|살며시|느리|낮은\s*장력)[^,.;!?…\n]{0,18}(?:따라|감기|둥글|흐르|접히|스미|한\s*바퀴)/,
    /(?:따라|감기|둥글|흐르|접히|스미|한\s*바퀴)[^,.;!?…\n]{0,14}(?:숨결|천천히|부드럽|살며시|느리|낮은\s*장력)/,
  ],
  named: [
    /[‘“'"][^’”'"]{1,14}[’”'"][^,.;!?…\n]{0,10}(?:부르|불러|붙이|명명)/,
    /(?:이름|호칭|매듭)[^,.;!?…\n]{0,12}(?:받|부르|불러|붙이|명명)/,
  ],
  cyclic: [
    /(?:둥글|되돌|되풀이|반복|한\s*바퀴|돌고|뒤집)[^,.;!?…\n]{0,14}(?:따라|다시|원래|받은|시작)/,
    /(?:따라|다시|원래|받은|시작)[^,.;!?…\n]{0,14}(?:둥글|되돌|되풀이|반복|한\s*바퀴|돌고|뒤집)/,
  ],
  timed: [
    /(?:첫|한\s*번|두\s*번|제시간|박자|순서|마지막|다음\s*(?:울림|차례)|번째)[^,.;!?…\n]{0,16}(?:면|때|후|뒤|마다|까지|하자|고\s*나서)/,
  ],
  remembering: [
    /(?:오래된|앞선|이전|과거|빌린|흔적|원본)[^,.;!?…\n]{0,16}(?:약속|기억|간직|보존|남겨|되새|돌려주)/,
  ],
  reciprocal: [
    /(?:돌려주|되돌려주|반환|건네|놓아주)[^,.;!?…\n]{0,16}(?:답|응답|노크|받|주인|서로|한\s*번씩)/,
    /(?:답|응답|노크|받|서로|한\s*번씩)[^,.;!?…\n]{0,16}(?:돌려주|되돌려주|반환|건네|놓아주)/,
    /(?:주인|받은)[^,.;!?…\n]{0,14}(?:돌려주|돌려준|반환|보내)/,
  ],
  reflective: [
    /(?:거울|표면|뒷면|반사면|얼굴|그림자)[^,.;!?…\n]{0,18}(?:뒤집|비추|반사|거꾸로|반대로|방향을?\s*바꿔)/,
    /(?:뒤집|비추|반사|거꾸로|반대로)[^,.;!?…\n]{0,14}(?:거울|표면|뒷면|반사면|얼굴|그림자)/,
  ],
  released: [
    /(?:붙잡|붙들|가두|묶|빌린|받은|훔친|소유|이름|얼굴)[^,.;!?…\n]{0,18}(?:놓아주|풀어주|돌려보내|해방|보내\s*주|돌려준다)/,
  ],
  forceful: [
    /(?:억지|강제로|해야만|명령|모든|모조리|전부|누구도|아무도)[^,.;!?…\n]{0,18}(?:끌어당|밀어붙|베어|부수|깨뜨|삼키|붙잡|붙들|차지|지우|덮치|가두)/,
    /(?:끌어당|밀어붙|베어|부수|깨뜨|삼키|붙잡|붙들|차지|지우|덮치|가두)[^,.;!?…\n]{0,12}(?:억지|강제로|해야만|명령)/,
  ],
  endless: [
    /(?:끝없|영원|한없이|계속|바닥\s*없|마지막이?\s*없|끝나지|멎지|닫히지)[^,.;!?…\n]{0,18}(?:번지|이어|울리|붙잡|붙들|기다리|되풀이|반복|요구|남아|커지)/,
  ],
  hungry: [
    /(?:삼키|먹어|먹고|허기|배고|굶주|소비)[^,.;!?…\n]{0,18}(?:다음|더\s*많|또\s|계속|마다|새로운|요구)/,
    /(?:다음|더\s*많|또\s|계속|마다|새로운)[^,.;!?…\n]{0,18}(?:삼키|먹어|먹고|허기|배고|굶주|소비|요구)/,
    /(?:삼키고\s*더|다음\s*이름을\s*삼키|더\s*많은[^,.;!?…\n]{0,8}요구)/,
  ],
  forgetful: [
    /(?:이름|주인|원본|얼굴|흔적|약속|매듭)[^,.;!?…\n]{0,18}(?:지우|잊|없이|사라지|대신\s*(?:그\s*)?자리)/,
    /(?:지우|잊|사라지)[^,.;!?…\n]{0,12}(?:이름|주인|원본|얼굴|흔적|약속)/,
    /(?:이름\s*없이|주인\s*없이|원본\s*없이|이름을\s*지우|얼굴을\s*지우)/,
  ],
}

function trimBounds(sentence: string, bounds: TextBounds): TextBounds {
  let { start, end } = bounds
  while (start < end && /\s/.test(sentence[start] ?? '')) start += 1
  while (end > start && /\s/.test(sentence[end - 1] ?? '')) end -= 1
  return { start, end }
}

function clauseBounds(sentence: string): readonly TextBounds[] {
  const clauses: TextBounds[] = []
  let start = 0

  for (let index = 0; index < sentence.length; index += 1) {
    if (!PUNCTUATION.test(sentence[index] ?? '')) continue
    const trimmed = trimBounds(sentence, { start, end: index + 1 })
    if (trimmed.end > trimmed.start) clauses.push(trimmed)
    start = index + 1
  }

  const tail = trimBounds(sentence, { start, end: sentence.length })
  if (tail.end > tail.start) clauses.push(tail)
  return clauses
}

function containingClause(
  sentence: string,
  position: number,
): TextBounds {
  return clauseBounds(sentence).find(
    ({ start, end }) => position >= start && position < end,
  ) ?? { start: 0, end: sentence.length }
}

function cueBounds(sentence: string, trait: Trait): TextBounds | null {
  for (const pattern of DISPLAY_ONLY_EXCERPT_ANCHORS[trait]) {
    const match = pattern.exec(sentence)
    if (match === null) continue
    // If two related cues cannot fit in the visible window, showing only one
    // would falsely suggest a word-to-trait rule. Use sentence basis instead.
    if (match[0].length > MAX_EXCERPT_LENGTH) continue
    return { start: match.index, end: match.index + match[0].length }
  }
  return null
}

function fitExcerpt(
  sentence: string,
  rawBounds: TextBounds,
  limitBounds: TextBounds,
): TextBounds {
  let { start, end } = trimBounds(sentence, rawBounds)
  const limit = trimBounds(sentence, limitBounds)
  const isBoundary = (value: string): boolean =>
    /\s/.test(value) || PUNCTUATION.test(value)

  if (end - start > MAX_EXCERPT_LENGTH) {
    end = start + MAX_EXCERPT_LENGTH
    const visible = sentence.slice(start, end)
    const lastSpace = visible.lastIndexOf(' ')
    if (lastSpace >= MIN_EXCERPT_LENGTH) end = start + lastSpace
  }

  // Regex cues intentionally stop at semantic stems. Complete their surface
  // words so the highlighted phrase reads naturally to the player.
  while (
    start > limit.start &&
    !isBoundary(sentence[start - 1] ?? '') &&
    end - (start - 1) <= MAX_EXCERPT_LENGTH
  ) {
    start -= 1
  }
  while (
    end < limit.end &&
    !isBoundary(sentence[end] ?? '') &&
    end + 1 - start <= MAX_EXCERPT_LENGTH
  ) {
    end += 1
  }

  while (end - start < Math.min(MIN_EXCERPT_LENGTH, sentence.length)) {
    const previousWindowStart = start
    const previousWindowEnd = end
    let nextEnd = end
    while (nextEnd < limit.end && /\s/.test(sentence[nextEnd] ?? '')) nextEnd += 1
    while (nextEnd < limit.end && !isBoundary(sentence[nextEnd] ?? '')) nextEnd += 1
    if (nextEnd > end && nextEnd - start <= MAX_EXCERPT_LENGTH) {
      end = nextEnd
    } else {
      let previousStart = start
      while (previousStart > limit.start && /\s/.test(sentence[previousStart - 1] ?? '')) {
        previousStart -= 1
      }
      while (previousStart > limit.start && !isBoundary(sentence[previousStart - 1] ?? '')) {
        previousStart -= 1
      }
      if (previousStart < start && end - previousStart <= MAX_EXCERPT_LENGTH) {
        start = previousStart
      }
    }
    if (start === limit.start && end === limit.end) break
    if (start === previousWindowStart && end === previousWindowEnd) break
  }

  // A very short punctuation clause may need its neighbour to reach 8 chars.
  while (end - start < Math.min(MIN_EXCERPT_LENGTH, sentence.length)) {
    if (start > 0) start -= 1
    if (end - start >= MIN_EXCERPT_LENGTH) break
    if (end < sentence.length) end += 1
    if (start === 0 && end === sentence.length) break
  }

  if (end - start > MAX_EXCERPT_LENGTH) end = start + MAX_EXCERPT_LENGTH
  return trimBounds(sentence, { start, end })
}

function fallbackBounds(sentence: string): TextBounds {
  return trimBounds(sentence, { start: 0, end: sentence.length })
}

/**
 * Maps already-confirmed traits back to deterministic display excerpts.
 * Contract: output length, order, and trait identity exactly mirror `traits`.
 * Every offset points into `sentence`; no trait can be created or removed.
 */
export function anchorConfirmedTraits(
  sentence: string,
  traits: readonly Trait[],
): readonly DisplayExcerptAnchor[] {
  return traits.map((trait) => {
    const cue = cueBounds(sentence, trait)
    const fallback = fallbackBounds(sentence)
    const rawBounds = cue ?? fallback
    const limit = cue === null
      ? fallback
      : containingClause(sentence, cue.start)
    const { start, end } = fitExcerpt(sentence, rawBounds, limit)

    return {
      trait,
      excerpt: sentence.slice(start, end),
      basis: cue === null ? 'sentence' : 'clause',
      start,
      end,
    }
  })
}
